import React, { useEffect, useState, useCallback } from "react";
import { Badge, Menu } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSignInAlt,
  faLongArrowAltUp,
} from "@fortawesome/free-solid-svg-icons";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import firebase from "../FirebaseConfig.js";
import { UNLOGGED, LOGGED } from "../util/constants.js";
import * as actions from "redux/popup/actions.js";
import { useDispatch, useSelector } from "react-redux";
import Popup from "./Popup/index.js";
import { resolveCustomerLogo } from "util/resolveCustomerLogo";
import { Avatar } from "antd";
import "./topMenu.scss";
import { themeColor } from "util/common.js";
import { useTranslation } from "react-i18next";
import { CONSOLE_HOME_PATH, resolveConsoleHomePath } from "util/consoleHome";
import NotificationPage from "components/Notifcation/index.js";
import SystemNotification from "components/SystemNotification/index.js";
import { Button, notification, Tooltip } from "antd";
import { copyToClipboard } from "util/common";
import {
  noticeNotificationQueryKey,
  fetchNoticeNotificationAll,
  derivePanelStateFromNoticeResponse,
  deriveUnreadBadgeCount,
  noticeBadgeQueryOptions,
} from "queries/noticeNotifications";
import { pinNavbarIntoView } from "./AppShellScrollLock";
import WhatsNewDrawer from "pages/WhatsNew/WhatsNewDrawer";
import ClaudeAnnouncement from "components/ClaudeAnnouncement";
import {
  fetchWhatsNewList,
  markAllWhatsNewRead,
  markWhatsNewRead,
} from "pages/WhatsNew/whatsNewApi";

const { SubMenu } = Menu;

const TopMenu = ({ authenticated, user, theme }) => {
  const [selectedMenuItem, setSelectedMenuItem] = useState([]);
  const history = useHistory();
  const [showLogin, setShowLogin] = useState("");
  const [showKCenterPass, setshowKCenterPass] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [filterUnread, setFilterUnread] = useState(2);
  const [notificationList, setNotificationList] = useState([]);
  const [tempList, setTempList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNoUnreadAvailable, setShowNoUnreadAvailable] = useState(false);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [showLast30Days, setShowLast30Days] = useState(false);
  const [notifsCount, setNotifsCount] = useState(0);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [whatsNewEntries, setWhatsNewEntries] = useState([]);
  const [whatsNewUnread, setWhatsNewUnread] = useState(0);
  const dispatch = useDispatch();
  const userReducer = useSelector((state) => state.userReducer);
  const customerLogo = resolveCustomerLogo(userReducer, user);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const uid = user?.uid;
  const loggedIn = authenticated !== UNLOGGED;

  const badgeQuery = useQuery({
    queryKey: noticeNotificationQueryKey(uid, 2),
    queryFn: () => fetchNoticeNotificationAll(uid, 2),
    enabled: Boolean(uid && loggedIn),
    ...noticeBadgeQueryOptions,
    onError: () =>
      notification.error({ message: t("notification_page.1") }),
  });

  const listQuery = useQuery({
    queryKey: noticeNotificationQueryKey(uid, filterUnread),
    queryFn: () => fetchNoticeNotificationAll(uid, filterUnread),
    enabled: Boolean(uid && loggedIn && showNotification),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    onError: () =>
      notification.error({ message: t("notification_page.1") }),
  });

  useEffect(() => {
    setNotifsCount(deriveUnreadBadgeCount(badgeQuery.data));
  }, [badgeQuery.data]);

  useEffect(() => {
    const d = derivePanelStateFromNoticeResponse(
      listQuery.data,
      showLast30Days,
    );
    if (!d) return;
    setShowNoUnreadAvailable(d.showNoUnreadAvailable);
    setNotificationList(d.notificationList);
    setTempList(d.tempList);
    setShowEmptyState(d.showEmptyState);
  }, [listQuery.data, showLast30Days]);

  const invalidateNoticeQueries = useCallback(async () => {
    if (!uid) return;
    await queryClient.invalidateQueries({
      queryKey: ["notice", "notification", "all", uid],
    });
  }, [queryClient, uid]);

  const applyWhatsNewPayload = useCallback((payload) => {
    if (!payload) return;
    setWhatsNewEntries(payload.entries || []);
    setWhatsNewUnread(payload.unread_count || 0);
  }, []);

  useEffect(() => {
    if (!uid || !loggedIn) return undefined;
    fetchWhatsNewList(i18n.language).then(applyWhatsNewPayload);
    return undefined;
  }, [uid, loggedIn, i18n.language, applyWhatsNewPayload]);

  useEffect(() => {
    if (authenticated === UNLOGGED) {
      return undefined;
    }
    const unlisten = history.listen(() => {
      setSelectedMenuItem(document.location.pathname.split("/")[1]);
    });
    setSelectedMenuItem(document.location.pathname.split("/")[1]);
    return () => unlisten();
  }, [authenticated, history]);

  const panelListFetching = showNotification && listQuery.isFetching;

  const copyPassword = () => {
    copyToClipboard("axgQ167");
    notification.success({
      message: t("notifications.54"),
    });
  };

  const handleClick = (e) => {
    if (e.key === "popup") {
      dispatch({
        type: actions.OPEN_POPUP,
      });
    } else if (e.key === "tutorials") {
      const win = window.open(
        "https://pivony.notion.site/Pivony-Knowledge-Center-59a7a883a48349a6975bf47576edc407",
        "_blank"
      );
    } else if (e.key === "courses") {
      const win = window.open("https://learning.pivony.com/", "_blank");
      win.focus();
    } else if (e.key === "/") {
      document.location.href = document.location.origin;
    } else if (e.key === "feedback") {
      const win = window.open(
        "mailto:support@pivony.com?subject=Internal%20Support%20Ticket%20Submission",
        "_blank"
      );
      win.focus();
    } else if (e.key !== "logout") {
      history.push(e.key);
    }
  };

  const signout = () => {
    firebase
      .auth()
      .signOut()
      .then(
        () => {
          // Else the next account to sign in here sends this one's JWT to users/token.
          window.localStorage.removeItem("jwt_token");
          window.localStorage.removeItem("selectedDashboard");
          document.location.href = document.location.origin + "/login";
        },
        function(error) {
          console.error("Sign Out Error", error);
        }
      );
  };

  const KnowledgeCenterPopup = () => {
    return (
      <div className="popup">
        <div className="content">
          <div className="header">
            <div className="texts">
              <div className="title">Please Check Your Password!</div>
              <div className="description">
                you will be asked to enter this password to access the knowledge
                Center
              </div>
            </div>

            <div
              className="close"
              onClick={() => {
                setshowKCenterPass(false);
              }}
            >
              <img
                src={theme === "dark" ? "/assets/x.svg" : "/assets/x_black.svg"}
                alt="help"
              />{" "}
            </div>
          </div>
          <div className="inputWrapper">
            <input
              type="text"
              placeholder="Enter text"
              value={"Partners12."}
              disabled
            />
            <Button className="button" onClick={copyPassword}>
              {" "}
              <img
                src={theme === "dark" ? "/assets/copy.svg" : "/assets/copy.svg"}
                alt="help"
              />{" "}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (
      window.location.pathname === "/signup" ||
      window.location.pathname === "/"
    ) {
      setShowLogin("Login");
    }
    if (window.location.pathname === "/login") {
      setShowLogin("Signup");
    }
  }, [location.pathname]);

  useEffect(() => {
    const el = document.getElementById("header_to_menu");
    if (el?.style?.display === "none") {
      el.style.display = "";
    }
    pinNavbarIntoView();
  }, [location.pathname, location.search]);

  return (
    <div
      id="header_to_menu"
      style={{
        backgroundColor: themeColor(theme),
        minHeight: 60,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div className="nav-topbar-inner">
        <a
          href={
            authenticated === LOGGED && user.plan
              ? user.plan == 68
                ? document.location.origin + "/sentiment_intent_analyzer"
                : document.location.origin + resolveConsoleHomePath(user, userReducer)
              : "//pivony.com"
          }
        >
          <div className="nav-logo">
            <div className="nav-logo-container">
              <Tooltip title="Home" placement="right">
                {theme === "brand" ? (
                  <img src="/assets/pivony-logo1.png" alt="logo" />
                ) : (
                  <img src="/assets/pivony-logo.png" alt="logo" />
                )}
              </Tooltip>
            </div>
          </div>
        </a>

        {authenticated === LOGGED && customerLogo ? (
          <div className="nav-customer-logo">
            <img src={customerLogo} alt="" />
          </div>
        ) : null}

        <div className="nav-topbar-right">
          <Menu
            onClick={handleClick}
            selectedKeys={[selectedMenuItem]}
            mode="horizontal"
            theme="dark"
            className="top-navigation-bar"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              backgroundColor: themeColor(theme),
            }}
          >
        {authenticated === LOGGED && (
          <button
            type="button"
            className="topbar-whats-new"
            key="whats-new"
            onClick={() => {
              setShowNotification(false);
              setShowWhatsNew((prev) => !prev);
            }}
          >
            <Badge
              count={whatsNewUnread}
              style={{
                backgroundColor: theme !== "brand" ? "#E60000" : "#4400AA",
                boxShadow: "none",
              }}
            >
              <span className="topbar-whats-new-label">
                {t("whats_new.title")}
              </span>
            </Badge>
          </button>
        )}
        {authenticated === LOGGED && (
          <div
            className={`topbar-notification-icon${notifsCount > 0 ? " has-unread" : ""}`}
            key="notification"
            onClick={() => {
              setShowWhatsNew(false);
              setShowNotification((prev) => {
                if (!prev) {
                  // Panel opening: reset to "show all" and fetch fresh data
                  setFilterUnread(2);
                  setShowLast30Days(false);
                }
                return !prev;
              });
            }}
          >
            <Badge
              count={notifsCount}
              style={{
                backgroundColor: theme !== "brand" ? "#E60000" : "#4400AA",
                boxShadow: "none",
              }}
            >
              <img src="/assets/bell.svg" alt="" />
            </Badge>
          </div>
        )}
        <SubMenu
          id="help"
          className="sub-menu-item"
          popupClassName="sub-menu-item"
          title={
            <span>
              <img src="/assets/help-circle.svg" alt="help" />
            </span>
          }
        >
          {" "}
          <Menu.Item key="tutorials" className="underline-item">
            {t("top_menu.2")}{" "}
            {theme === "dark" ? (
              <img
                src="/assets/arrow-up-right.svg"
                alt=""
                width={14}
                height={14}
              />
            ) : (
              <img
                src="/assets/arrow-up-right_black.svg"
                alt=""
                width={14}
                height={14}
              />
            )}
          </Menu.Item>
          <Menu.Item key="courses" className="underline-item">
            {t("top_menu.3")}{" "}
            {theme === "dark" ? (
              <img
                src="/assets/arrow-up-right.svg"
                alt=""
                width={14}
                height={14}
              />
            ) : (
              <img
                src="/assets/arrow-up-right_black.svg"
                alt=""
                width={14}
                height={14}
              />
            )}
          </Menu.Item>
          <Menu.Item key="feedback" className="underline-item">
            {t("top_menu.6")}{" "}
            {theme === "dark" ? (
              <img
                src="/assets/arrow-up-right.svg"
                alt=""
                width={14}
                height={14}
              />
            ) : (
              <img
                src="/assets/arrow-up-right_black.svg"
                alt=""
                width={14}
                height={14}
              />
            )}
          </Menu.Item>
        </SubMenu>
        {authenticated === LOGGED && (
          <SubMenu
            id="myaccount"
            style={{ color: "#fff", width: "24px", height: "24px" }}
            className="avatar-box"
            popupClassName="avatar-submenu"
            title={
              <span className="avatar-icon">
                <span className="user-name-letter">
                  {user?.firstName
                    ? user.firstName.charAt(0).toUpperCase()
                    : user?.email
                    ? user?.email?.charAt(0).toUpperCase()
                    : "P"}
                </span>
              </span>
            }
          >
            {user.plan && (
              <Menu.Item
                key="/settings"
                icon={
                  theme === "dark" ? (
                    <img src="/assets/settings.svg" alt="" />
                  ) : (
                    <img src="/assets/settings_black.svg" alt="" />
                  )
                }
                className="avatar-item"
              >
                {t("top_menu.4")}
              </Menu.Item>
            )}
            <Menu.Item
              onClick={signout}
              key="logout"
              icon={
                theme === "dark" ? (
                  <img src="/assets/log-out.svg" alt="" />
                ) : (
                  <img src="/assets/log-out-black.svg" alt="" />
                )
              }
              className="avatar-item"
            >
              {t("top_menu.5")}
            </Menu.Item>
          </SubMenu>
        )}
        {authenticated === UNLOGGED && showLogin === "Signup" && (
          <Menu.Item
            key="/signup"
            icon={<FontAwesomeIcon icon={faLongArrowAltUp} />}
            style={{ float: "right" }}
            className="avatar-item"
          >
            Signup
          </Menu.Item>
        )}
        {authenticated === UNLOGGED && showLogin === "Login" && (
          <Menu.Item
            key="/login"
            icon={<FontAwesomeIcon icon={faSignInAlt} />}
            style={{ float: "right" }}
            className="avatar-item"
          >
            Login
          </Menu.Item>
        )}
          </Menu>
          {authenticated === LOGGED &&
            window.location.href.split("/").includes("login") === false && (
              <div className="nav-topbar-system-notification">
                <SystemNotification />
              </div>
            )}
        </div>
      </div>

      {showKCenterPass ? <KnowledgeCenterPopup /> : ""}
      <Popup user={user} auth={authenticated} />
      <ClaudeAnnouncement
        uid={uid}
        enabled={authenticated === LOGGED && location.pathname.startsWith("/console")}
      />
      {showWhatsNew && (
        <WhatsNewDrawer
          entries={whatsNewEntries}
          canEdit={false}
          onSeeAll={() => {
            setShowWhatsNew(false);
            history.push("/console/whats-new");
          }}
          onMarkAll={async () => {
            const payload = await markAllWhatsNewRead(i18n.language);
            applyWhatsNewPayload(payload);
          }}
          onOpenEntry={async (item) => {
            setShowWhatsNew(false);
            if (!item.is_read) {
              const payload = await markWhatsNewRead(item.id, i18n.language);
              applyWhatsNewPayload(payload);
            }
            history.push(`/console/whats-new/${item.id}`);
          }}
        />
      )}
      {showNotification && (
        <NotificationPage
          user={user}
          theme={theme}
          setShowNotification={setShowNotification}
          filterUnread={filterUnread}
          setFilterUnread={setFilterUnread}
          notificationList={notificationList}
          setNotificationList={setNotificationList}
          showNoUnreadAvailable={showNoUnreadAvailable}
          setShowNoUnreadAvailable={setShowNoUnreadAvailable}
          tempList={tempList}
          setTempList={setTempList}
          loading={loading}
          setLoading={setLoading}
          showEmptyState={showEmptyState}
          setShowEmptyState={setShowEmptyState}
          showLast30Days={showLast30Days}
          setShowLast30Days={setShowLast30Days}
          invalidateNoticeQueries={invalidateNoticeQueries}
          setNotifsCount={setNotifsCount}
          onViewAll={(tab) => {
            setShowNotification(false);
            history.push(`/console/notifications?tab=${tab || "notifications"}`);
          }}
        />
      )}
    </div>
  );
};

export default TopMenu;
